<template>
  <div class="flex flex-col gap-y-4">
    <ScheduleStepHeading :title="SCHEDULE_SECTION_LABELS.basicInfo" class="mb-2">
      <OutlineButton
        v-if="flags.enabledPolicyModules"
        size="small"
        width="auto"
        height="32px"
        :prependIcon="mdiContentDuplicate"
        data-testid="import-schedule-config-button"
        @click="emit('openImportModal')"
      >
        Importar configurações
      </OutlineButton>
    </ScheduleStepHeading>

    <v-row dense class="gap-4">
      <v-col cols="12">
        <v-text-field
          v-model="step1.scheduleName"
          label="Nome da Escala"
          variant="outlined"
          color="primary20"
          :error="!!props.errors.scheduleName"
          :error-messages="props.errors.scheduleName"
          hide-details="auto"
          @input="props.onClearError('scheduleName')"
        />
      </v-col>

      <v-row dense>
        <v-col cols="12" md="8">
          <v-autocomplete
            v-model="step1.timezone"
            label="Selecione o fuso-horário"
            :items="timezones"
            variant="outlined"
            color="primary20"
            :error="!!props.errors.timezone"
            :error-messages="props.errors.timezone"
            hide-details="auto"
            @update:modelValue="props.onClearError('timezone')"
          >
            <template #item="{ props, item }">
              <v-list-item v-bind="props" :title="item.raw?.title" :subtitle="item.raw?.subtitle" />
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="4">
          <DatePicker v-model="step1.endDate" locale="pt-BR" :masks="{ input: `DD/MM/YYYY` }">
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                label="Final da escala"
                :append-inner-icon="mdiCalendarBlankOutline"
                :model-value="inputValue"
                v-on="inputEvents"
                color="primary20"
                variant="outlined"
                hide-details="auto"
                :error="!!props.errors.endDate"
                :error-messages="props.errors.endDate"
                @focus="props.onClearError('endDate')"
              />
            </template>
          </DatePicker>
        </v-col>
      </v-row>

      <v-col cols="12">
        <v-autocomplete
          v-model="step1.healthUnitId"
          label="Selecione a unidade vinculada"
          :items="healthUnitsAdminAccess"
          :loading="isLoadingSelectFetch"
          no-data-text="Nenhuma unidade encontrada"
          variant="outlined"
          color="primary20"
          :error="!!props.errors.healthUnitId"
          :error-messages="props.errors.healthUnitId"
          hide-details="auto"
          @update:modelValue="handleHealthUnitSelection"
        >
          <template #item="{ props, item }">
            <v-list-item v-bind="props" :title="item.raw?.title" :subtitle="item.raw?.subtitle" />
          </template>
        </v-autocomplete>
        <p
          class="cursor-pointer text-left text-sm text-primary mb-2 mt-1"
          @click="handleOpenModal('create')"
        >
          Deseja cadastrar uma nova unidade?
        </p>
      </v-col>

      <v-col cols="12">
        <OperationalGroupSelect
          :model-value="operationalGroupSelection"
          @update:model-value="
            (selection) => {
              props.onClearError('operationalGroup')
              updateOperationalGroup(selection)
            }
          "
          :disabled="!step1.healthUnitId"
          :items="operationalGroupOptions"
          :error="!!props.errors.operationalGroup"
          :error-messages="props.errors.operationalGroup"
        />
      </v-col>
    </v-row>
  </div>

  <!-- Tipo de agenda (Demanda, SPEC 2.2 §5.5; mockup D: antes da
       proporcionalidade): na criação quem cria a escala edita. -->
  <AgendaTypeField
    v-if="flags.enabledDashboardDemand"
    v-model="step1.agendaType"
    mode="create"
    :readonly="false"
    :detection="null"
  />

  <p class="font-bold text-base text-[#323232] text-left mb-4 mt-4">Regras de proporcionalidade</p>

  <div class="flex gap-6">
    <div v-for="opt in proportionalityRuleOptions" :key="opt.value" class="flex-1">
      <SelectableCard
        :title="opt.title"
        :description="opt.description"
        :selected="step1.proportionality === opt.value"
        :error="!!props.errors.proportionality"
        :errorMessage="props.errors.proportionality"
        @click="
          () => {
            step1.proportionality = opt.value
            props.onClearError('proportionality')
          }
        "
      />
    </div>
  </div>

  <p class="font-bold text-base text-[#323232] text-left mb-2 mt-4">
    Lembrete de confirmação pelo aplicativo
  </p>
  <div class="flex gap-2">
    <v-checkbox
      v-model="step1.teamDoctorReminderEnabled"
      label="Lembrete para médico interno"
      density="compact"
      color="primary"
      hide-details="auto"
      :disabled="true"
      class="w-1/2"
    />

    <v-checkbox
      v-model="step1.externalDoctorReminderEnabled"
      label="Lembrete para médico externo"
      density="compact"
      color="primary"
      hide-details="auto"
      class="w-1/2"
    />
  </div>

  <HealthUnitModal
    v-if="toggleHealthUnitModal"
    :healthUnit="healthUnitToUpdate"
    :modalStateProp="toggleHealthUnitModal"
    @close="closeHealthUnitModal"
    @created="handleUnitCreated"
    :type="operationModal"
  />
</template>

<script setup lang="ts">
import { ref, onMounted, computed, inject, watch } from 'vue'
import { timezones } from '@/hooks/useCommon'
import SelectableCard from '@/components/designSystem/Cards/SelectableCard.vue'
import AgendaTypeField from '@/components/ManageScale/Schedule/AgendaTypeField.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import type {
  HealthUnitResponse,
  SimpleHealthUnitResponse
} from '@/Network/Types/Responses/HealthUnitResponses'
import HealthUnitModal from '@/modules/company/Units/modals/UnitFormModal.vue'
import { storeToRefs } from 'pinia'
import { useCreateScheduleStore } from '@/pinia-store/useCreateScheduleStore'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { proportionalityRuleOptions } from '@/utils/schedule'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import { useErrorToast } from '@/composables/useErrorToast'
import OperationalGroupSelect from '@/components/designSystem/Select/OperationalGroupSelect.vue'
import { mdiCalendarBlankOutline, mdiContentDuplicate } from '@mdi/js'
import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { SCHEDULE_SECTION_LABELS } from '@/constants/schedule-sections.constants'
import ScheduleStepHeading from './ScheduleStepHeading.vue'

const store = (inject('createScheduleStore') ?? useCreateScheduleStore()) as ReturnType<
  typeof useCreateScheduleStore
>
const { step1 } = storeToRefs(store)
const { flags } = useFeatureFlags()

store.markAsDirty()

const operationModal = ref<'create'>('create')
const toggleHealthUnitModal = ref(false)
const healthUnitToUpdate = ref<SimpleHealthUnitResponse>()

const healthUnitStore = useHealthUnitStore()
const { healthUnitsAdminAccess, isLoadingSelectFetch } = storeToRefs(healthUnitStore)

const handleOpenModal = (operation: 'create') => {
  operationModal.value = operation
  toggleHealthUnitModal.value = true
}
const closeHealthUnitModal = async () => {
  toggleHealthUnitModal.value = false
}
const handleUnitCreated = async (newHealthUnit: HealthUnitResponse) => {
  await healthUnitStore.getHealthUnitOptions(true)
  step1.value.healthUnitId = newHealthUnit.id
  handleHealthUnitSelection(newHealthUnit.id)
}

const operationalGroupSelection = ref<{ id: number; name: string } | null>(null)
const allOperationalGroups = ref<{ id: number; name: string }[]>([])
const fieldOptionsService = new FieldOptionsService()
const { notifyError } = useErrorToast()

function updateOperationalGroup(selection: { id: number; name: string } | string | null) {
  let groupNameToSave: string | null = null
  let groupObjectToDisplay: { id: number; name: string } | null = null

  if (typeof selection === 'object' && selection !== null) {
    groupNameToSave = selection.name
    groupObjectToDisplay = selection
  } else if (typeof selection === 'string' && selection.length > 0) {
    groupNameToSave = selection
    groupObjectToDisplay = allOperationalGroups.value.find((g) => g.name === selection) || null
  }

  step1.value.operationalGroup = groupNameToSave
  operationalGroupSelection.value = groupObjectToDisplay
}

onMounted(async () => {
  if (!step1.value.timezone) {
    step1.value.timezone = '-03:00'
  }

  try {
    // O mapeamento do valor atual de step1.operationalGroup (nome) para o objeto
    // {id, name} que o OperationalGroupSelect precisa fica a cargo do watch abaixo —
    // ele reage à chegada de `allOperationalGroups` (esta linha) do mesmo jeito que
    // reage a mudanças programáticas de operationalGroup depois do mount.
    allOperationalGroups.value = await fieldOptionsService.getOperationalGroups()
  } catch (error) {
    notifyError(error, 'Não foi possível carregar os grupos operacionais.')
  }
})

/**
 * Mantém `operationalGroupSelection` (o objeto {id, name} que o
 * OperationalGroupSelect exibe) sincronizado com `step1.operationalGroup` (a
 * string que a store guarda).
 *
 * Sem este watch, preenchimento programático da store DEPOIS do mount (ex.: o
 * import de configurações de outra escala, que roda em resposta a um clique do
 * usuário, bem depois do onMounted) não passa por `updateOperationalGroup` — o
 * campo Grupo Operacional aparece vazio mesmo com valor salvo na store.
 *
 * Observar `allOperationalGroups` também cobre o caso em que a lista de grupos
 * ainda não tinha chegado quando `operationalGroup` mudou (o fetch acima é
 * assíncrono) — quando ela chega, a busca é refeita.
 *
 * Guard de igualdade evita ciclo: `updateOperationalGroup` já escreve os dois
 * refs juntos, então quando ESTE watch dispara em reação a essa escrita, o
 * valor já está correto e a atualização é pulada.
 */
watch([() => step1.value.operationalGroup, allOperationalGroups], ([groupName]) => {
  if (operationalGroupSelection.value?.name === groupName) return

  operationalGroupSelection.value = groupName
    ? (allOperationalGroups.value.find((g) => g.name === groupName) ?? null)
    : null
})

function handleHealthUnitSelection(selectedId: number | null) {
  props.onClearError('healthUnitId')
  if (!selectedId) {
    step1.value.healthUnitName = ''
    updateOperationalGroup(null)
    return
  }

  const selectedUnit = healthUnitsAdminAccess.value.find((unit) => unit.value === selectedId)
  step1.value.healthUnitName = selectedUnit?.title || ''

  updateOperationalGroup(selectedUnit?.operationalGroup || null)
}

const operationalGroupOptions = computed(() => {
  if (!step1.value.healthUnitId) {
    return []
  }
  return allOperationalGroups.value
})

const emit = defineEmits<{ openImportModal: [] }>()

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<{
  errors: Record<string, string>
  onClearError: (field: string) => void
}>()
</script>
